import React, { useState } from "react";
import { withRouter } from "react-router";
import { Redirect, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import axios from "axios";
import Swal from "sweetalert2";

import { subirArchivo } from "../../utils/utilidadesArchivos";
import useObtenerMunicipio from "../../utils/useObtenerMunicipio";
import useTituloPagina from "../../utils/useTituloPagina";

import FormInput from "../components/forminput";
import FormTextArea from "../components/formtextarea";
import PasosPresentacion from "../components/pasosvotacion";
import MPage from "../templates/mpage";
import Cargando from "../components/cargando";
import ErrorMensaje from "../components/errormensaje";

//Formatea un tamaño en bytes a KB/MB legibles.
const formatoTam = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const NOMBRES_PASOS = ["Tus datos", "La propuesta", "Adjuntos y envío"];

const FormPresentacion = (props) => {
  const [municipio, error] = useObtenerMunicipio(props.match.params.munUrl);
  const [imagen, guardarImagen] = useState("");
  const [documento, guardarDocumento] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [paso, setPaso] = useState(1);
  const [dniDisplay, setDniDisplay] = useState("");
  const [presupuestoDisplay, setPresupuestoDisplay] = useState("");
  useTituloPagina(
    municipio ? `Presentar una propuesta — ${municipio.nombre}` : null
  );

  const {
    register,
    formState: { errors },
    setValue,
    getValues,
    setError,
    clearErrors,
  } = useForm();

  //Formatea el DNI con puntos mientras se escribe (mismo criterio que el
  //formulario de votación); el valor guardado en el form son solo los dígitos.
  const formatDNI = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 8);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6)
      return `${digits.slice(0, digits.length - 3)}.${digits.slice(-3)}`;
    return `${digits.slice(0, digits.length - 6)}.${digits.slice(
      digits.length - 6,
      digits.length - 3
    )}.${digits.slice(-3)}`;
  };

  const handleDniChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
    setDniDisplay(formatDNI(e.target.value));
    setValue("dni", digits, { shouldValidate: true });
  };

  //Formatea el presupuesto con puntos de miles mientras se escribe; el valor
  //guardado (y enviado) son solo los dígitos.
  const formatMiles = (digits) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

  const handlePresupuestoChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    setPresupuestoDisplay(formatMiles(digits));
    setValue("presupuesto", digits, { shouldValidate: false });
  };

  const validarFecha = (dia, mes, anio) => {
    if (!dia || !mes || !anio) return true;
    const d = parseInt(dia);
    const m = parseInt(mes) - 1;
    const a = parseInt(anio);
    const fecha = new Date(a, m, d);
    return (
      fecha.getFullYear() === a &&
      fecha.getMonth() === m &&
      fecha.getDate() === d
    );
  };

  //Guarda la imagen cargada en el formulario
  const actualizarImagen = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        Swal.fire(
          "Revisá el archivo",
          "Solo se permiten imágenes (JPG o PNG).",
          "error"
        );
        e.target.value = "";
        return;
      }
      guardarImagen(file);
    }
  };

  //Guarda el documento cargado en el formulario
  const actualizarDocumento = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        Swal.fire("Revisá el archivo", "Solo se permiten archivos PDF.", "error");
        e.target.value = "";
        return;
      }
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        Swal.fire(
          "El archivo es muy grande",
          "El PDF no puede superar los 5MB.",
          "error"
        );
        e.target.value = "";
        return;
      }
      guardarDocumento(file);
    }
  };

  //Campos obligatorios de cada paso, para validar solo esos al avanzar.
  const camposPaso = {
    1: ["autor", "dni", "diaNac", "mesNac", "anioNac", "email", "tel", "direccion"],
    2: [
      "titulo",
      "subtitulo",
      ...(municipio && municipio.ejes.length > 0 ? ["eje"] : []),
      "presupuesto",
      "objetivos",
      "beneficiarios",
      "laccion",
      "resultados",
      "pejecucion",
      "descripcion",
    ],
  };

  const irArriba = () => window.scrollTo(0, 0);

  //Devuelve true si el valor está cargado (no vacío).
  const noVacio = (val) =>
    val !== undefined && val !== null && String(val).trim() !== "";

  //Valida el paso con los valores reales del formulario y devuelve el id del
  //primer campo inválido (o null si está todo bien). No se usa el booleano de
  //trigger porque en esta versión de react-hook-form devuelve la validez del
  //formulario COMPLETO, no la del campo, y siempre daría inválido mientras los
  //pasos siguientes estén vacíos.
  const primerCampoInvalido = (p) => {
    const val = getValues();
    if (p === 1) {
      if (!noVacio(val.autor)) return "autor";
      const dniDigits = String(val.dni || "").replace(/\D/g, "");
      if (dniDigits.length < 7 || dniDigits.length > 8) return "dni";
      if (!noVacio(val.diaNac)) return "diaNac";
      if (!noVacio(val.mesNac)) return "mesNac";
      if (!noVacio(val.anioNac)) return "anioNac";
      if (!validarFecha(val.diaNac, val.mesNac, val.anioNac)) return "diaNac";
      if (!noVacio(val.email)) return "email";
      if (!noVacio(val.tel)) return "tel";
      if (!noVacio(val.direccion)) return "direccion";
    }
    if (p === 2) {
      if (!noVacio(val.titulo)) return "titulo";
      if (!noVacio(val.subtitulo)) return "subtitulo";
      if (municipio.ejes.length > 0 && (!noVacio(val.eje) || val.eje === "0"))
        return "eje";
      if (String(val.presupuesto || "").replace(/\D/g, "").length === 0)
        return "presupuesto";
      if (!noVacio(val.objetivos)) return "objetivos";
      if (!noVacio(val.beneficiarios)) return "beneficiarios";
      if (!noVacio(val.laccion)) return "laccion";
      if (!noVacio(val.resultados)) return "resultados";
      if (!noVacio(val.pejecucion)) return "pejecucion";
      if (!noVacio(val.descripcion)) return "descripcion";
    }
    return null;
  };

  //Lleva el foco (y el scroll) a un campo por su id, tras el próximo render.
  const enfocarCampo = (id) => {
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        if (el.focus) el.focus({ preventScroll: true });
      }
    }, 0);
  };

  const siguiente = () => {
    clearErrors(camposPaso[paso]);
    const invalido = primerCampoInvalido(paso);
    if (!invalido) {
      setPaso((p) => p + 1);
      irArriba();
    } else {
      //Marca el error en línea y lleva el foco al primer campo con problema
      //(así no parece que "no pasa nada" con el botón al pie de la página).
      setError(invalido, { type: "manual" });
      enfocarCampo(invalido);
    }
  };

  const anterior = () => {
    setPaso((p) => Math.max(1, p - 1));
    irArriba();
  };

  //Envío final: valida a mano los pasos 1 y 2 (no se usa handleSubmit de RHF,
  //que en esta versión valida el formulario completo de forma poco fiable) y,
  //si está todo bien, arma los datos y llama a onSubmit.
  const enviarFinal = () => {
    for (const p of [1, 2]) {
      clearErrors(camposPaso[p]);
      const invalido = primerCampoInvalido(p);
      if (invalido) {
        setPaso(p);
        setError(invalido, { type: "manual" });
        enfocarCampo(invalido);
        return;
      }
    }
    onSubmit(getValues());
  };

  const onSubmit = async (data) => {
    setEnviando(true);
    data["municipioId"] = municipio._id;
    data["img"] = "pdefault.svg";
    data["doc"] = "";

    //Fecha de nacimiento armada desde los tres campos (día/mes/año).
    data["fechaNacimiento"] = `${data["anioNac"]}-${data["mesNac"].padStart(
      2,
      "0"
    )}-${String(data["diaNac"]).padStart(2, "0")}`;
    delete data["diaNac"];
    delete data["mesNac"];
    delete data["anioNac"];

    //El DNI se guarda con puntos en el formulario; se manda solo con dígitos.
    data["dni"] = String(data["dni"] || "").replace(/\D/g, "");
    //El presupuesto se muestra con puntos de miles; se manda como número.
    data["presupuesto"] = Number(
      String(data["presupuesto"] || "").replace(/\D/g, "")
    );

    // Subir imagen si existe
    if (imagen && imagen.size > 0) {
      try {
        const nuevoNombre = await subirArchivo(imagen);
        if (nuevoNombre === null) {
          setEnviando(false);
          Swal.fire(
            "Error",
            "Ocurrió un error al subir la imagen de la propuesta, intentá de nuevo.",
            "error"
          );
          return;
        }
        data["img"] = nuevoNombre;
      } catch (error) {
        setEnviando(false);
        Swal.fire(
          "Error",
          "Ocurrió un error al subir la imagen de la propuesta, intentá de nuevo.",
          "error"
        );
        return;
      }
    }

    if (documento && documento !== "") {
      try {
        const nuevoNombre = await subirArchivo(documento);
        if (nuevoNombre === null) {
          setEnviando(false);
          Swal.fire(
            "Error",
            "Ocurrió un error al subir la documentación anexa, intentá de nuevo.",
            "error"
          );
          return;
        }
        data["doc"] = nuevoNombre;
      } catch (error) {
        setEnviando(false);
        Swal.fire(
          "Error",
          "Ocurrió un error al subir la documentación anexa, intentá de nuevo.",
          "error"
        );
        return;
      }
    }

    //Guardo la propuesta
    try {
      await axios.post("/api/propuestas/", data);
      Swal.fire(
        "¡Tu propuesta se envió con éxito!",
        "Vamos a revisarla y pronto vas a poder seguirla desde la página principal.",
        "success"
      );
      props.history.push(`/municipio/${municipio.nombreUrl}`);
    } catch (err) {
      setEnviando(false);
      //El servidor puede rechazar la propuesta si la presentación se cerró
      //mientras el vecino completaba el formulario.
      Swal.fire(
        err.response && err.response.data && err.response.data.errorMessage
          ? err.response.data.errorMessage
          : "Ocurrió un error al subir la propuesta. Volvé a intentarlo o ponete en contacto con el municipio.",
        "",
        "error"
      );
    }
  };

  if (error === true)
    return (
      <ErrorMensaje
        titulo="No encontramos este municipio"
        mensaje="Puede que el enlace esté mal escrito o que el municipio no esté disponible."
      />
    );

  if (municipio === undefined) return <Cargando />;
  if (municipio.etapa !== "presentacion")
    return <Redirect to={`/municipio/${municipio.nombreUrl}`} />;

  //Valores actuales, para la pantalla de revisión del paso 3.
  const v = getValues();
  const mesesNombre = [
    "",
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];

  return (
    <MPage municipio={municipio}>
      <PasosPresentacion
        pasos={NOMBRES_PASOS}
        pasoActual={paso}
        etiqueta="Progreso de la presentación"
      />

      <h1 className="ppr-titulo mt-3 centrado h3">
        Formulario de Presentación de Propuesta
      </h1>
      <p className="mt-2 mb-1">
        En la sección{" "}
        <Link to={"/municipio/" + municipio.nombreUrl + "/info"} className="ppr-a">
          Más información
        </Link>{" "}
        encontrás documentación detallada sobre el presupuesto participativo.
        Ante cualquier duda, escribinos: encontrá nuestros datos en la{" "}
        <Link
          to={"/municipio/" + municipio.nombreUrl + "/contacto"}
          className="ppr-a"
        >
          página de contacto
        </Link>
        .
      </p>
      <small className="text-muted">
        Los campos indicados con <b>*</b> son obligatorios.
      </small>

      {/*Se previene el submit nativo (Enter o cambio de tipo de botón): el
         envío se hace solo con el botón "Presentar propuesta" del último paso,
         para que no se dispare antes de tiempo.*/}
      <form className="mt-3" onSubmit={(e) => e.preventDefault()}>
        {/* ----------------------- PASO 1: TUS DATOS ----------------------- */}
        {paso === 1 && (
          <fieldset>
            <h2 className="h5 font-weight-bold">Sobre el autor</h2>
            <FormInput
              register={register}
              error={errors.autor}
              label={"* Autor:"}
              id={"autor"}
              isRequired={true}
              ayuda="Nombre y apellido del autor, o nombre de la organización, según corresponda."
              placeholder="Ej: María Pérez"
              autoComplete="name"
            />
            <FormInput
              register={register}
              error={errors.equipo}
              label={"Equipo:"}
              id={"equipo"}
              isRequired={false}
              ayuda="Si es una organización, describí brevemente el equipo."
              placeholder="Opcional"
            />

            <div className="form-group">
              <label htmlFor="dni">* DNI:</label>
              <input
                {...register("dni", {
                  required: true,
                  validate: (value) => {
                    const digits = String(value || "").replace(/\D/g, "");
                    return digits.length >= 7 && digits.length <= 8;
                  },
                })}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                className={`form-control ${errors.dni ? "is-invalid" : ""}`}
                id="dni"
                placeholder="Ej: 12.345.678"
                value={dniDisplay}
                onChange={handleDniChange}
              />
              {errors.dni && (
                <small className="text-danger form-text">
                  Ingresá un DNI válido (7 u 8 dígitos).
                </small>
              )}
            </div>

            <div className="form-group">
              <label>* Fecha de nacimiento:</label>
              <div className="row">
                <div className="col-3 col-md-2">
                  <input
                    {...register("diaNac", {
                      required: true,
                      min: 1,
                      max: 31,
                      validate: (value) => {
                        const { mesNac, anioNac } = getValues();
                        return (
                          validarFecha(value, mesNac, anioNac) || "Fecha inválida"
                        );
                      },
                    })}
                    type="number"
                    inputMode="numeric"
                    autoComplete="bday-day"
                    placeholder="Día"
                    min="1"
                    max="31"
                    className={`form-control ${
                      errors.diaNac ? "is-invalid" : ""
                    }`}
                  />
                  {errors.diaNac && (
                    <small className="text-danger form-text">
                      {errors.diaNac.message || "Día inválido."}
                    </small>
                  )}
                </div>
                <div className="col-5 col-md-4">
                  <select
                    {...register("mesNac", { required: true })}
                    autoComplete="bday-month"
                    className={`form-control ${
                      errors.mesNac ? "is-invalid" : ""
                    }`}
                  >
                    <option value="">Mes</option>
                    <option value="01">Enero</option>
                    <option value="02">Febrero</option>
                    <option value="03">Marzo</option>
                    <option value="04">Abril</option>
                    <option value="05">Mayo</option>
                    <option value="06">Junio</option>
                    <option value="07">Julio</option>
                    <option value="08">Agosto</option>
                    <option value="09">Septiembre</option>
                    <option value="10">Octubre</option>
                    <option value="11">Noviembre</option>
                    <option value="12">Diciembre</option>
                  </select>
                  {errors.mesNac && (
                    <small className="text-danger form-text">
                      Seleccioná un mes.
                    </small>
                  )}
                </div>
                <div className="col-4 col-md-3">
                  <input
                    {...register("anioNac", {
                      required: true,
                      min: 1900,
                      valueAsNumber: true,
                    })}
                    type="number"
                    inputMode="numeric"
                    autoComplete="bday-year"
                    placeholder="Año"
                    min="1900"
                    max={new Date().getFullYear()}
                    className={`form-control ${
                      errors.anioNac ? "is-invalid" : ""
                    }`}
                  />
                  {errors.anioNac && (
                    <small className="text-danger form-text">
                      {`Año inválido (1900-${new Date().getFullYear()}).`}
                    </small>
                  )}
                </div>
              </div>
            </div>

            <FormInput
              register={register}
              error={errors.email}
              label={"* Correo electrónico:"}
              id={"email"}
              isRequired={true}
              placeholder="Ej: tunombre@correo.com"
              type="email"
              autoComplete="email"
            />
            <FormInput
              register={register}
              error={errors.tel}
              label={"* Teléfono:"}
              id={"tel"}
              isRequired={true}
              ayuda="No olvides el código de área."
              placeholder="Ej: 351 123 4567"
              type="tel"
              autoComplete="tel"
            />
            <FormInput
              register={register}
              error={errors.direccion}
              label={"* Dirección:"}
              id={"direccion"}
              isRequired={true}
              placeholder="Ej: Av. San Martín 123"
              type="text"
              autoComplete="street-address"
            />
          </fieldset>
        )}

        {/* --------------------- PASO 2: LA PROPUESTA --------------------- */}
        {paso === 2 && (
          <fieldset>
            <h2 className="h5 font-weight-bold">Sobre la propuesta</h2>
            <FormInput
              register={register}
              error={errors.titulo}
              label={"* Título:"}
              id={"titulo"}
              isRequired={true}
              placeholder="Un título breve y claro"
            />
            <FormInput
              register={register}
              error={errors.subtitulo}
              label={"* Subtítulo:"}
              id={"subtitulo"}
              isRequired={true}
              placeholder="Una frase que resuma la propuesta"
            />
            {municipio.ejes.length > 0 && (
              <div className="form-group">
                <label htmlFor="eje">* Eje de la propuesta:</label>
                <select
                  className={`form-control ${errors.eje ? "is-invalid" : ""}`}
                  id="eje"
                  defaultValue="0"
                  {...register("eje", {
                    validate: (value) => value !== "0",
                    required: true,
                  })}
                >
                  <option key="placeholder" value="0" disabled>
                    Elegí un eje para tu propuesta.
                  </option>
                  {municipio.ejes.map((eje) => (
                    <option key={eje} value={eje}>
                      {eje}
                    </option>
                  ))}
                </select>
                {errors.eje && (
                  <small className="text-danger form-text">
                    Campo Obligatorio.
                  </small>
                )}
              </div>
            )}
            <div className="form-group">
              <label htmlFor="presupuesto">* Presupuesto:</label>
              <small className="form-text text-muted mb-1">
                Monto aproximado para ejecutar la propuesta.
              </small>
              <input
                {...register("presupuesto", { required: true })}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                id="presupuesto"
                className={`form-control ${
                  errors.presupuesto ? "is-invalid" : ""
                }`}
                placeholder="Ej: 1.500.000"
                value={presupuestoDisplay}
                onChange={handlePresupuestoChange}
              />
              {errors.presupuesto && (
                <small className="text-danger form-text">
                  Ingresá un monto.
                </small>
              )}
            </div>
            <FormTextArea
              register={register}
              error={errors.objetivos}
              label={"* Objetivos:"}
              id={"objetivos"}
              isRequired={true}
              ayuda="Detallá, de forma clara y concreta, qué se quiere lograr con este proyecto."
              placeholder="Escribí acá los objetivos..."
              rows={6}
            />
            <FormTextArea
              register={register}
              error={errors.beneficiarios}
              label={"* Beneficiarios:"}
              id={"beneficiarios"}
              isRequired={true}
              ayuda="A quién o quiénes se dirige la propuesta: el grupo o sector impactado de forma directa."
              placeholder="Escribí acá los beneficiarios..."
              rows={6}
            />
            <FormTextArea
              register={register}
              error={errors.laccion}
              label={"* Lugar de acción:"}
              id={"laccion"}
              isRequired={true}
              ayuda="La ubicación o área geográfica donde se desarrollará la propuesta."
              placeholder="Ej: Barrio Centro"
              rows={2}
            />
            <FormTextArea
              register={register}
              error={errors.resultados}
              label={"* Resultados esperados:"}
              id={"resultados"}
              isRequired={true}
              ayuda="Los beneficios, tangibles e intangibles, que se obtendrán con el proyecto."
              placeholder="Escribí acá los resultados esperados..."
              rows={6}
            />
            <FormTextArea
              register={register}
              error={errors.pejecucion}
              label={"* Periodo de ejecución:"}
              id={"pejecucion"}
              isRequired={true}
              ayuda="Duración estimada de la propuesta, en meses."
              placeholder="Ej: 6 meses"
              rows={2}
            />
            <FormTextArea
              register={register}
              error={errors.descripcion}
              label={"* Descripción:"}
              id={"descripcion"}
              isRequired={true}
              ayuda="Describí la propuesta con el mayor detalle posible."
              placeholder="Escribí acá la descripción..."
              rows={8}
            />
          </fieldset>
        )}

        {/* ----------------- PASO 3: ADJUNTOS + REVISIÓN ----------------- */}
        {paso === 3 && (
          <fieldset>
            <h2 className="h5 font-weight-bold">Adjuntos (opcionales)</h2>

            <div className="form-group">
              <label htmlFor="doc">Documentación (PDF, máximo 5MB):</label>
              <small className="form-text text-muted mb-1">
                Opcional. Podés adjuntar un PDF con más detalle.
              </small>
              <input
                type="file"
                id="doc"
                accept=".pdf,application/pdf"
                className="form-control-file"
                onChange={actualizarDocumento}
              />
              {documento && documento.name && (
                <small className="form-text ppr-color-azul font-weight-bold">
                  ✓ {documento.name} ({formatoTam(documento.size)})
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="img">Imagen (.jpg o .png):</label>
              <small className="form-text text-muted mb-1">
                Opcional. Una imagen ayuda a que tu propuesta se destaque.
              </small>
              <input
                type="file"
                id="img"
                accept="image/png, image/jpeg"
                className="form-control-file"
                onChange={actualizarImagen}
              />
              {imagen && imagen.name && (
                <small className="form-text ppr-color-azul font-weight-bold">
                  ✓ {imagen.name} ({formatoTam(imagen.size)})
                </small>
              )}
            </div>

            <h2 className="h5 font-weight-bold mt-4">Revisá tu propuesta</h2>
            <small className="form-text text-muted mb-2">
              Comprobá que los datos sean correctos. Podés volver atrás para
              corregir cualquier cosa antes de enviar.
            </small>
            <div className="card">
              <div className="card-body">
                <dl className="row mb-0">
                  <dt className="col-5 col-sm-4">Autor</dt>
                  <dd className="col-7 col-sm-8">{v.autor}</dd>
                  {v.equipo && (
                    <>
                      <dt className="col-5 col-sm-4">Equipo</dt>
                      <dd className="col-7 col-sm-8">{v.equipo}</dd>
                    </>
                  )}
                  <dt className="col-5 col-sm-4">DNI</dt>
                  <dd className="col-7 col-sm-8">{dniDisplay}</dd>
                  <dt className="col-5 col-sm-4">Nacimiento</dt>
                  <dd className="col-7 col-sm-8">
                    {v.diaNac} de {mesesNombre[parseInt(v.mesNac || "0", 10)]} de{" "}
                    {v.anioNac}
                  </dd>
                  <dt className="col-5 col-sm-4">Email</dt>
                  <dd className="col-7 col-sm-8">{v.email}</dd>
                  <dt className="col-5 col-sm-4">Teléfono</dt>
                  <dd className="col-7 col-sm-8">{v.tel}</dd>
                  <dt className="col-5 col-sm-4">Dirección</dt>
                  <dd className="col-7 col-sm-8">{v.direccion}</dd>

                  <dt className="col-12 mt-2 ppr-color-azul">Propuesta</dt>
                  <dt className="col-5 col-sm-4">Título</dt>
                  <dd className="col-7 col-sm-8">{v.titulo}</dd>
                  <dt className="col-5 col-sm-4">Subtítulo</dt>
                  <dd className="col-7 col-sm-8">{v.subtitulo}</dd>
                  {v.eje && (
                    <>
                      <dt className="col-5 col-sm-4">Eje</dt>
                      <dd className="col-7 col-sm-8">{v.eje}</dd>
                    </>
                  )}
                  <dt className="col-5 col-sm-4">Presupuesto</dt>
                  <dd className="col-7 col-sm-8">${presupuestoDisplay}</dd>
                </dl>
              </div>
            </div>
          </fieldset>
        )}

        {/* --------------------- NAVEGACIÓN DE PASOS --------------------- */}
        <div className="d-flex justify-content-between align-items-center mt-4 mb-2">
          {paso > 1 ? (
            <button
              type="button"
              onClick={anterior}
              className="btn ppr-btn-azul font-weight-bold"
            >
              ← Anterior
            </button>
          ) : (
            <Link
              to={"/municipio/" + municipio.nombreUrl}
              className="btn ppr-btn-azul font-weight-bold"
            >
              Cancelar
            </Link>
          )}

          {paso < 3 ? (
            <button
              type="button"
              onClick={siguiente}
              className="btn ppr-btn-verde font-weight-bold"
            >
              Siguiente →
            </button>
          ) : (
            <button
              type="button"
              onClick={enviarFinal}
              className="btn ppr-btn-verde font-weight-bold"
              disabled={enviando}
            >
              {enviando ? "Enviando..." : "Presentar propuesta"}
            </button>
          )}
        </div>
      </form>
    </MPage>
  );
};

export default withRouter(FormPresentacion);
